import type {
  MissingSkuItemDto,
  MissingSkuQueryParams,
  MissingSkuReason,
  StockImportLogModel,
} from '../types/stockImports.types'
import type {
  MissingSkuIssueType,
  PriceIssueReason,
} from '../types/stockImportsPrices.types'
import { toExcelRowNumber } from './stockImportsFormat'
import {
  formatMissingSkuReason,
  MISSING_SKU_REASON_LABELS,
} from './stockImportsMessages'

export type MissingSkuIssueFilter = MissingSkuIssueType | 'ALL'

export interface MissingSkuFilters {
  article?: string
  reasons: MissingSkuReason[]
  hideZeroStock: boolean
  issueType: MissingSkuIssueFilter
  priceReasons: PriceIssueReason[]
}

export type MissingSkuReportState = 'available' | 'deleted' | 'none'

export const DEFAULT_MISSING_SKU_FILTERS: MissingSkuFilters = {
  reasons: [],
  hideZeroStock: true,
  issueType: 'STOCK',
  priceReasons: [],
}

export const MISSING_SKU_REASON_OPTIONS = (
  Object.keys(MISSING_SKU_REASON_LABELS) as MissingSkuReason[]
).map((reason) => ({
  value: reason,
  label: MISSING_SKU_REASON_LABELS[reason],
}))

export const toMissingSkuQueryParams = ({
  article,
  reasons,
  hideZeroStock,
  issueType,
  priceReasons,
}: MissingSkuFilters): MissingSkuQueryParams => ({
  ...(article ? { article } : {}),
  ...(reasons.length > 0 ? { reasons: reasons.join(',') } : {}),
  ...(hideZeroStock ? { zeroStock: false as const } : {}),
  ...(issueType === 'ALL' ? {} : { issueType }),
  ...(issueType !== 'STOCK' && priceReasons.length > 0
    ? { priceReasons: priceReasons.join(',') }
    : {}),
})

export const toMissingSkuExportParams = ({
  article,
  reasons,
  zeroStock,
}: MissingSkuQueryParams): MissingSkuQueryParams => ({
  ...(article ? { article } : {}),
  ...(reasons ? { reasons } : {}),
  ...(zeroStock === false ? { zeroStock } : {}),
  issueType: 'STOCK',
})

export interface MissingSkuExportFilterItem {
  label: string
  details?: string[]
}

export const describeMissingSkuExport = (
  filters: MissingSkuFilters,
  filteredCount: number,
  totalCount: number | undefined
) => {
  const items: MissingSkuExportFilterItem[] = [
    ...(filters.reasons.length > 0
      ? [
          {
            label: filters.reasons.length > 1 ? 'Причины:' : 'Причина:',
            details: filters.reasons.map(
              (reason) => MISSING_SKU_REASON_LABELS[reason]
            ),
          },
        ]
      : []),
    ...(filters.hideZeroStock
      ? [{ label: 'Нулевые остатки по найденным товарам скрыты' }]
      : []),
    ...(filters.article
      ? [{ label: `Артикул содержит «${filters.article}»` }]
      : []),
  ]

  if (items.length === 0) {
    return {
      title: `Будут выгружены все незагруженные строки — ${totalCount ?? filteredCount}.`,
      items,
    }
  }

  const countText =
    totalCount === undefined
      ? `${filteredCount}`
      : `${filteredCount} из ${totalCount}`

  return {
    title: `Будут выгружены строки по фильтрам — ${countText}:`,
    items,
  }
}

export const toMissingSkuExportRows = (items: MissingSkuItemDto[]) =>
  items.map((item) => ({
    Строка: toExcelRowNumber(item.rowIndex),
    Бренд: item.brand ?? '',
    Наименование: item.description ?? '',
    'Артикул (производителя)': item.article ?? '',
    'Артикул (поставщика)': item.articleInch ?? '',
    Штрихкод: item.barcode ?? '',
    Остаток: item.quantity ?? '',
    Файл: item.fileName ?? '',
    Лист: item.sheetName ?? '',
    Причина: formatMissingSkuReason(item.reason, item.searchedBy),
  }))

export const getMissingSkuReportState = (
  log: Pick<StockImportLogModel, 'hasMissingSkuData' | 'missingRows'>
): MissingSkuReportState => {
  if (log.hasMissingSkuData) return 'available'
  return log.missingRows > 0 ? 'deleted' : 'none'
}
